import * as React from 'react';
import { Tooltip as TooltipPrimitive } from 'radix-ui';
import { InfoIcon } from 'lucide-react';
import { cva } from 'class-variance-authority';

import { mergeClasses } from '@/utils';

import { SvgIcon } from '@/components/common';

interface IInformationTooltip extends React.ComponentProps<typeof TooltipPrimitive.Root> {
  size?: 'sm' | 'default' | 'lg';
  side?: 'top' | 'right' | 'bottom' | 'left';
  triggerClassName?: string;
  contentClassName?: string;
}

const INFORMATION_TOOLTIP_SIZE_VARIANTS = cva('', {
  variants: {
    variant: {
      default: '!max-w-[320px]',
      sm: '!max-w-[240px]',
      lg: '!max-w-[480px]',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const TooltipProvider = ({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) => {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
};

const Tooltip = ({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) => {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
};

const TooltipTrigger = ({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) => {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
};

const TooltipContent = ({
  className,
  sideOffset = 0,
  children,
  size = 'default',
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & {
  size?: 'sm' | 'default' | 'lg';
}) => {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={mergeClasses(
          'bg-neutral-9 text-neutral-0 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 inline-flex items-center gap-1.5 rounded-m px-3 py-2 text-xs has-data-[slot=kbd]:pr-1.5 **:data-[slot=kbd]:relative **:data-[slot=kbd]:isolate **:data-[slot=kbd]:z-50 **:data-[slot=kbd]:rounded-small z-50 w-fit max-w-xs origin-(--radix-tooltip-content-transform-origin)',
          INFORMATION_TOOLTIP_SIZE_VARIANTS({ variant: size }),
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className="size-2.5 rotate-45 rounded-[2px] z-50 translate-y-[calc(-50%_-_2px)] bg-neutral-9 fill-neutral-9" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
};

const InformationTooltip = ({
  children,
  size = 'default',
  side = 'bottom',
  triggerClassName,
  contentClassName,
  ...props
}: IInformationTooltip) => {
  return (
    <Tooltip {...props}>
      <TooltipTrigger className={mergeClasses('cursor-default align-middle', triggerClassName)}>
        <SvgIcon component={InfoIcon} className="size-4 text-neutral-6" />
      </TooltipTrigger>
      <TooltipContent
        className={mergeClasses('text-center', contentClassName)}
        size={size}
        side={side}
      >
        {children}
      </TooltipContent>
    </Tooltip>
  );
};

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, InformationTooltip };
